import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Device } from '@eon/types';
import { api } from '../client';

interface KeyResponse {
  key: string;
  ttl: number;
}

export interface DeviceUpdate {
  type: 'device-update';
  deviceId: string; // OCPP chargeBoxId, i.e. serialNumber
  status?: string;
  lastHeartbeatAt?: string | null;
  connector?: {
    id: number;
    status?: string;
    errorCode?: string;
    info?: string;
  };
  reason?: string;
  ts?: string;
}

/**
 * useDeviceUpdates opens a global event-hub WebSocket and patches the React
 * Query cache when device-update envelopes arrive from the server.
 *
 * Mount this once at the top of the admin app (e.g. in AppLayout). It does
 * not return any state — it just keeps caches fresh.
 */
export function useDeviceUpdates(enabled = true): void {
  const qc = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const retryRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout> | null = null;

    const apply = (u: DeviceUpdate) => {
      // Detail cache is keyed by device UUID, but the WS envelope only knows
      // the serial number (OCPP chargeBoxId). Walk every cached device entry
      // and patch the one whose serial matches.
      qc.getQueriesData<Device>({ queryKey: ['admin', 'devices'] }).forEach(([key, cached]) => {
        if (!cached) return;
        // List queries store a Page<Device>, detail stores a Device. Disambiguate
        // by shape.
        if ('data' in (cached as unknown as { data?: unknown }) && Array.isArray((cached as unknown as { data: Device[] }).data)) {
          const page = cached as unknown as { data: Device[] };
          let touched = false;
          const next = page.data.map((d) => {
            if (d.serialNumber !== u.deviceId) return d;
            touched = true;
            return mergeDevice(d, u);
          });
          if (touched) {
            qc.setQueryData(key, { ...page, data: next });
          }
        } else {
          const d = cached as Device;
          if (d?.serialNumber === u.deviceId) {
            qc.setQueryData(key, mergeDevice(d, u));
          }
        }
      });
      // Counts may have shifted (e.g. status changed); cheap to refetch.
      qc.invalidateQueries({ queryKey: ['admin', 'devices', 'counts'] });
    };

    const connect = async () => {
      try {
        const { key } = await api().post<KeyResponse>('/event-hub');
        if (cancelled) return;
        const baseURL = api().baseURL.replace(/^http/, 'ws');
        const ws = new WebSocket(`${baseURL}/event-hub?key=${encodeURIComponent(key)}`);
        wsRef.current = ws;
        ws.onopen = () => {
          retryRef.current = 0;
        };
        ws.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data) as Partial<DeviceUpdate>;
            if (data.type !== 'device-update' || !data.deviceId) return;
            apply(data as DeviceUpdate);
          } catch {
            /* ignore */
          }
        };
        ws.onclose = () => {
          if (cancelled) return;
          const delay = Math.min(30_000, 1_000 * 2 ** retryRef.current++);
          timeout = setTimeout(connect, delay);
        };
        ws.onerror = () => ws.close();
      } catch {
        if (cancelled) return;
        const delay = Math.min(30_000, 1_000 * 2 ** retryRef.current++);
        timeout = setTimeout(connect, delay);
      }
    };
    void connect();
    return () => {
      cancelled = true;
      if (timeout) clearTimeout(timeout);
      wsRef.current?.close();
    };
  }, [enabled, qc]);
}

const HEARTBEAT_STALE_MS = 90_000;

function heartbeatAgeMs(ts: string | null | undefined): number | null {
  if (!ts) return null;
  const ms = Date.parse(ts);
  if (Number.isNaN(ms)) return null;
  return Date.now() - ms;
}

/** Matches backend/mobile: online when heartbeat is fresh even if status is stale offline. */
export function isDeviceOnline(d: Device): boolean {
  const age = heartbeatAgeMs(d.lastHeartbeatAt ?? d.lastHeartbeat);
  if (age != null && age >= 0 && age <= HEARTBEAT_STALE_MS) return true;
  return d.status !== 'offline';
}

function mergeDevice(d: Device, u: DeviceUpdate): Device {
  const next: Device = { ...d };
  if (u.lastHeartbeatAt) {
    next.lastHeartbeatAt = u.lastHeartbeatAt;
    next.lastHeartbeat = u.lastHeartbeatAt;
    const age = heartbeatAgeMs(u.lastHeartbeatAt);
    if (age != null && age >= 0 && age <= HEARTBEAT_STALE_MS && next.status === 'offline') {
      next.status = 'available';
    }
  }
  if (u.status) {
    next.status = u.status as Device['status'];
  }
  if (u.connector) {
    const c = u.connector;
    const existing = d.connectors ?? [];
    const idx = existing.findIndex((x) => x.id === c.id);
    const merged = idx >= 0
      ? existing.map((x, i) =>
          i === idx
            ? {
                ...x,
                status: c.status ?? x.status,
                errorCode: c.errorCode ?? x.errorCode,
                info: c.info ?? x.info,
                updatedAt: u.ts ?? x.updatedAt,
              }
            : x,
        )
      : [
          ...existing,
          {
            id: c.id,
            status: c.status ?? '',
            errorCode: c.errorCode,
            info: c.info,
            updatedAt: u.ts,
          },
        ];
    next.connectors = merged;
  }
  return next;
}
